import { Suspense, useEffect, type ReactElement } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'

import { OrgChoicePage } from '@nebula/shared/components/OrgChoicePage'
import { useAuthStore } from '@nebula/shared/store'
import { lazyWithRetry } from '@nebula/shared/utils'

import LoginPage from './pages/LoginPage'

const MainLayout = lazyWithRetry(() => import('./layouts/MainLayout'))
const VerifyEmailPage = lazyWithRetry(() => import('./pages/VerifyEmailPage'))
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'))
const ProductsPage = lazyWithRetry(() => import('./pages/ProductsPage'))
// MarkupConfigPage removido — conteúdo absorvido pelo ProductsPage (aba "Regras de Markup")
const PortalOverviewPage = lazyWithRetry(() => import('./pages/PortalOverviewPage'))
const PortalConfigPage = lazyWithRetry(() => import('./pages/PortalConfigPage'))
const PortalAssetsPage = lazyWithRetry(() => import('./pages/PortalAssetsPage'))
const PortalDomainPage = lazyWithRetry(() => import('./pages/PortalDomainPage'))
const PortalAnalyticsPage = lazyWithRetry(() => import('./pages/PortalAnalyticsPage'))
const ContractsPage = lazyWithRetry(() => import('./pages/ContractsPage'))
const SpecialContractsPage = lazyWithRetry(() => import('./pages/SpecialContractsPage'))
const WalletPage = lazyWithRetry(() => import('./pages/WalletPage'))
const PaymentsPage = lazyWithRetry(() => import('./pages/PaymentsPage'))
const CreditManagementPage = lazyWithRetry(() => import('./pages/CreditManagementPage'))
const InvoicesPage = lazyWithRetry(() => import('./pages/InvoicesPage'))
const TitulosPage = lazyWithRetry(() => import('./pages/TitulosPage'))
const CreditCustomerDetailPage = lazyWithRetry(() => import('./pages/CreditCustomerDetailPage'))
const PaymentCustomerDetailPage = lazyWithRetry(() => import('./pages/PaymentCustomerDetailPage'))
const AssociationsPage = lazyWithRetry(() => import('./pages/AssociationsPage'))
// NetworkPoolsViewPage removido - RS não precisa mais de visualizacao de network pools
const SDNZonesPage = lazyWithRetry(() => import('./pages/SDNZonesPage'))
const OrganizationsPage = lazyWithRetry(() => import('./pages/OrganizationsPage'))
const PlatformSettingsPage = lazyWithRetry(() => import('./pages/PlatformSettingsPage'))
const InfrastructurePage = lazyWithRetry(() => import('./pages/InfrastructurePage'))
const GatewaysPage = lazyWithRetry(() => import('./pages/GatewaysPage'))
const CustomerInstancesPage = lazyWithRetry(() => import('./pages/CustomerInstancesPage'))
const TeamPage = lazyWithRetry(() => import('./pages/TeamPage'))
const AccountPage = lazyWithRetry(() => import('./pages/AccountPage'))
const SupportPage = lazyWithRetry(() => import('./pages/SupportPage'))
const BackupAnalyticsPage = lazyWithRetry(() => import('./pages/BackupAnalyticsPage'))
const NotificationsPage = lazyWithRetry(() => import('./pages/NotificationsPage'))
const InviteAcceptPage = lazyWithRetry(() =>
  import('@nebula/shared/components').then((module) => ({ default: module.InviteAcceptPage })),
)

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-3 size-7 animate-spin rounded-full border-b-2 border-green-600" />
        <p className="text-sm text-gray-600">Carregando página...</p>
      </div>
    </div>
  )
}

function withRouteFallback(element: ReactElement) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
}

function App() {
  const { user, token, checkAuth, isLoading, orgChoiceRequired } = useAuthStore()
  const location = useLocation()
  const isLoginPage = location.pathname === '/login' || location.pathname === '/'

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Redirect após login se veio de convite
  useEffect(() => {
    if (user) {
      const pendingRedirect = sessionStorage.getItem('invite_redirect')
      if (pendingRedirect) {
        sessionStorage.removeItem('invite_redirect')
        window.location.replace(pendingRedirect)
      }
    }
  }, [user])

  // Mostrar loading enquanto verifica autenticação (mas NÃO na página de login).
  // Só com token: sem ele o isLoading é o do login() em andamento, e trocar a LoginPage
  // pelo spinner a remontaria numa rota interna, perdendo o modal do código de 2FA (#1171)
  if (isLoading && !isLoginPage && token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-green-600"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  // Se não tem usuário, mostrar login ou rotas públicas
  if (!user) {
    // Allow verify-email and invite routes without authentication
    if (location.pathname === '/verify-email' || location.pathname.startsWith('/invite/')) {
      return (
        <Routes>
          <Route path="/verify-email" element={withRouteFallback(<VerifyEmailPage />)} />
          <Route path="/invite/:token" element={withRouteFallback(<InviteAcceptPage />)} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      )
    }

    return <LoginPage />
  }

  // Mais de uma org compatível e a aba não escolheu: pergunta antes de qualquer tela (#1145)
  if (orgChoiceRequired) {
    return <OrgChoicePage />
  }

  // Verificar tipo de organização (pode estar em organization.type ou organization_type)
  const organizationType = user?.organization?.type || user?.organization_type
  const hasAccess = organizationType === 'reseller'

  if (!hasAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-green-600">Acesso Negado</h1>
          <p className="mb-4 text-gray-600">Esta aplicação é restrita a Portal para Revendedores</p>
          <p className="mb-4 text-sm text-gray-500">
            Tipo da organização: {organizationType || 'Não definido'}
          </p>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
          >
            Fazer Logout
          </button>
        </div>
      </div>
    )
  }

  return (
    <Routes>
      {/* Invite accept page - outside MainLayout */}
      <Route path="/invite/:token" element={withRouteFallback(<InviteAcceptPage />)} />
      <Route path="/" element={withRouteFallback(<MainLayout />)}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={withRouteFallback(<DashboardPage />)} />
        <Route path="products" element={withRouteFallback(<ProductsPage />)} />
        <Route path="markup" element={<Navigate to="/products" replace />} />
        <Route path="contracts" element={withRouteFallback(<ContractsPage />)} />
        <Route path="special-contracts" element={withRouteFallback(<SpecialContractsPage />)} />
        {/* network-pools route removida - gerenciado automaticamente pelo VPC service */}
        <Route path="sdn-zones" element={withRouteFallback(<SDNZonesPage />)} />
        <Route path="infrastructure" element={withRouteFallback(<InfrastructurePage />)} />
        <Route path="gateways" element={withRouteFallback(<GatewaysPage />)} />
        <Route path="customer-instances" element={withRouteFallback(<CustomerInstancesPage />)} />
        <Route path="backup-analytics" element={withRouteFallback(<BackupAnalyticsPage />)} />
        <Route path="portal" element={withRouteFallback(<PortalOverviewPage />)} />
        <Route path="portal/config" element={withRouteFallback(<PortalConfigPage />)} />
        <Route path="portal/settings" element={withRouteFallback(<PortalConfigPage />)} />
        <Route path="portal/preview" element={withRouteFallback(<PortalConfigPage />)} />
        <Route path="portal/assets" element={withRouteFallback(<PortalAssetsPage />)} />
        <Route path="portal/domains" element={withRouteFallback(<PortalDomainPage />)} />
        <Route path="portal/analytics" element={withRouteFallback(<PortalAnalyticsPage />)} />
        <Route path="wallet" element={withRouteFallback(<WalletPage />)} />
        <Route path="payments" element={withRouteFallback(<PaymentsPage />)} />
        <Route
          path="payments/org/:customerId"
          element={withRouteFallback(<PaymentCustomerDetailPage />)}
        />
        <Route path="credit" element={withRouteFallback(<CreditManagementPage />)} />
        <Route
          path="credit/org/:customerId"
          element={withRouteFallback(<CreditCustomerDetailPage />)}
        />
        <Route path="invoices" element={withRouteFallback(<InvoicesPage />)} />
        <Route path="titulos" element={withRouteFallback(<TitulosPage />)} />
        <Route path="titulos/:tituloId" element={withRouteFallback(<TitulosPage />)} />
        <Route path="associations" element={withRouteFallback(<AssociationsPage />)} />
        <Route path="team" element={withRouteFallback(<TeamPage />)} />
        <Route path="support" element={withRouteFallback(<SupportPage />)} />
        <Route path="organizations" element={withRouteFallback(<OrganizationsPage />)} />
        <Route path="settings" element={withRouteFallback(<PlatformSettingsPage />)} />
        <Route path="profile" element={withRouteFallback(<AccountPage />)} />
        <Route path="notifications" element={withRouteFallback(<NotificationsPage />)} />
      </Route>
      {/* Redirect /login e rotas não encontradas para dashboard */}
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  )
}

export default App
